import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../api/client';
import MarkdownText from '../components/MarkdownText';

const INTENSITY_LABEL = {
  drill: 'долбить',
  drill_frame: 'долбить каркас',
  once: 'один раз',
  out: 'не в эталоне',
};

function examLabel(exam) {
  if (exam === '19-21') return '№19–21';
  return `№${exam}`;
}

export default function TeacherEgeMapPage() {
  const [tree, setTree] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedExam, setSelectedExam] = useState(null);
  const [selectedFamily, setSelectedFamily] = useState(null);
  const [selectedStep, setSelectedStep] = useState(null);
  const [selectedBasket, setSelectedBasket] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [tasksLoading, setTasksLoading] = useState(false);
  const [tasksError, setTasksError] = useState('');

  const loadTree = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/teacher/ege-map/');
      setTree(res.data);
      const exams = res.data?.exams || [];
      if (exams.length && selectedExam == null) {
        const first = exams.find((e) => e.placed > 0) || exams[0];
        setSelectedExam(first.exam);
      }
    } catch (e) {
      setError(
        e.response?.status === 403
          ? 'Доступ только для учителя.'
          : 'Не удалось загрузить карту.'
      );
      setTree(null);
    } finally {
      setLoading(false);
    }
  }, [selectedExam]);

  useEffect(() => {
    loadTree();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const examMeta = useMemo(() => {
    if (!tree?.exams) return null;
    return tree.exams.find((e) => e.exam === selectedExam) || null;
  }, [tree, selectedExam]);

  const loadTasks = useCallback(async () => {
    if (selectedExam == null && !selectedBasket) return;
    if (!selectedBasket && (!selectedFamily || !selectedStep)) {
      setTasks([]);
      return;
    }
    setTasksLoading(true);
    setTasksError('');
    try {
      const params = {};
      if (selectedExam != null && selectedBasket !== 'archive_word') {
        params.exam = String(selectedExam);
      }
      if (selectedBasket) params.basket = selectedBasket;
      else {
        params.family = selectedFamily;
        params.step = selectedStep.id || selectedStep;
      }
      const res = await api.get('/teacher/ege-map/tasks/', { params });
      setTasks(res.data?.tasks || []);
    } catch (e) {
      setTasks([]);
      setTasksError('Не удалось загрузить тексты.');
    } finally {
      setTasksLoading(false);
    }
  }, [selectedExam, selectedFamily, selectedStep, selectedBasket]);

  useEffect(() => {
    loadTasks();
  }, [loadTasks]);

  function selectExam(exam) {
    setSelectedExam(exam);
    setSelectedFamily(null);
    setSelectedStep(null);
    setSelectedBasket(null);
    setTasks([]);
  }

  function selectStep(family, step) {
    setSelectedFamily(family.id);
    setSelectedStep(step);
    setSelectedBasket(null);
  }

  function selectBasket(basketKey) {
    setSelectedBasket(basketKey);
    setSelectedFamily(null);
    setSelectedStep(null);
  }

  const stepTitle = useMemo(() => {
    if (selectedBasket) {
      return tree?.basket_labels?.[selectedBasket] || selectedBasket;
    }
    if (selectedStep) {
      const fam = examMeta?.families?.find((f) => f.id === selectedFamily);
      return `${fam?.name || selectedFamily} → ${selectedStep.name}`;
    }
    return null;
  }, [selectedBasket, selectedStep, selectedFamily, examMeta, tree]);

  if (loading) {
    return <p className="text-secondary">Загрузка карты…</p>;
  }

  if (error) {
    return <div className="alert alert-error">{error}</div>;
  }

  if (!tree) return null;

  const classified = new Set(tree.classified_exams || []);

  return (
    <div className="ege-map-page">
      <div className="page-header">
        <h1>Карта семей и ступеней</h1>
        <p className="page-subtitle text-secondary">
          Нумерация {tree.numbering}. Только для учителя — черновик раскладки журнальных
          заданий. Классифицированы: {[...(tree.classified_exams || [])].join(', ') || '—'}.
        </p>
      </div>

      <div className="ege-map-layout">
        <aside className="ege-map-exams" aria-label="Номера ЕГЭ">
          <ul>
            {(tree.exams || []).map((e) => {
              const disabled = e.exam === 27;
              const isActive = selectedExam === e.exam;
              const hasClass = classified.has(e.exam);
              return (
                <li key={String(e.exam)}>
                  <button
                    type="button"
                    className={`ege-map-exam-btn ${isActive ? 'active' : ''} ${disabled ? 'muted' : ''} ${hasClass ? 'classified' : ''}`}
                    onClick={() => selectExam(e.exam)}
                    title={
                      disabled
                        ? '№27 пока не трогаем'
                        : hasClass
                          ? 'Есть раскладка'
                          : 'Пока только ключи журнала'
                    }
                  >
                    <span>{examLabel(e.exam)}</span>
                    <span className="ege-map-exam-count">
                      {e.placed}/{e.total}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </aside>

        <section className="ege-map-families" aria-label="Семьи и ступени">
          {examMeta ? (
            <>
              <h2>{examLabel(examMeta.exam)}</h2>
              {examMeta.note ? (
                <div className="ege-map-note markdown-inline">
                  <MarkdownText text={examMeta.note} />
                </div>
              ) : null}
              {!examMeta.accepts_journal_tasks ? (
                <p className="text-secondary">Задания №27 в эту карту пока не кладём.</p>
              ) : null}

              <div className="ege-map-baskets">
                {Object.entries(examMeta.baskets || {})
                  .filter(([, n]) => n > 0)
                  .map(([key, n]) => (
                    <button
                      key={key}
                      type="button"
                      className={`btn btn-outline btn-sm ${selectedBasket === key ? 'active' : ''}`}
                      onClick={() => selectBasket(key)}
                    >
                      {(tree.basket_labels && tree.basket_labels[key]) || key}: {n}
                    </button>
                  ))}
              </div>

              <ul className="ege-map-family-list">
                {(examMeta.families || []).map((fam) => (
                  <li key={fam.id} className="ege-map-family">
                    <div className="ege-map-family-head">
                      <strong>{fam.name === '—' ? 'Корзина «Иное»' : fam.name}</strong>
                      <span className="text-secondary">{fam.count}</span>
                    </div>
                    <ul className="ege-map-step-list">
                      {(fam.steps || []).map((step) => {
                        const active =
                          !selectedBasket &&
                          selectedFamily === fam.id &&
                          selectedStep?.id === step.id;
                        return (
                          <li key={step.id}>
                            <button
                              type="button"
                              className={`ege-map-step-btn ${active ? 'active' : ''} ${step.count === 0 ? 'empty' : ''}`}
                              onClick={() => selectStep(fam, step)}
                            >
                              <span className="ege-map-step-name">{step.name}</span>
                              <span className="ege-map-step-meta">
                                <span className={`ege-map-intensity ege-map-intensity-${step.intensity}`}>
                                  {INTENSITY_LABEL[step.intensity] || step.intensity_label}
                                </span>
                                <span className="ege-map-step-count">{step.count}</span>
                              </span>
                            </button>
                            {step.look_for ? (
                              <div className="ege-map-look-for text-secondary">{step.look_for}</div>
                            ) : null}
                          </li>
                        );
                      })}
                    </ul>
                  </li>
                ))}
              </ul>
            </>
          ) : (
            <p className="text-secondary">Выберите номер слева.</p>
          )}
        </section>

        <section className="ege-map-tasks" aria-label="Тексты заданий">
          <h2>{stepTitle || 'Тексты'}</h2>
          {!stepTitle ? (
            <p className="text-secondary">Откройте ступень или корзину, чтобы листать условия.</p>
          ) : tasksLoading ? (
            <p className="text-secondary">Загрузка текстов…</p>
          ) : tasksError ? (
            <div className="alert alert-error">{tasksError}</div>
          ) : tasks.length === 0 ? (
            <p className="text-secondary">Пусто — сигнал добрать задания позже.</p>
          ) : (
            <ul className="ege-map-task-list">
              {tasks.map((t) => (
                <li key={t.journal_key || t.task_id} className="ege-map-task-card">
                  <div className="ege-map-task-head">
                    <strong>
                      {t.source_name || '—'} №{t.source_number || '?'}
                    </strong>
                    <span className="text-secondary">{t.journal_key}</span>
                    {t.confidence ? (
                      <span className={`ege-map-conf ege-map-conf-${t.confidence}`}>
                        {t.confidence}
                      </span>
                    ) : null}
                    {t.slug ? (
                      <Link to={`/work/${encodeURIComponent(t.slug)}`} className="btn btn-outline btn-sm">
                        Открыть
                      </Link>
                    ) : null}
                  </div>
                  {t.missing_text || !t.statement ? (
                    <p className="text-secondary">Текста в банке нет.</p>
                  ) : (
                    <div className="ege-map-task-statement markdown-body">
                      <MarkdownText text={t.statement} />
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
}
